import { api } from '../lib/axios';
import { Lesson, QuizQuestion } from '../types/lesson';
import { generateUuidV7 } from '@/utils/uuid';

/**
 * Get all lessons
 */
export const fetchLessons = async (): Promise<Lesson[]> => {
  try {
    const response = await api.get<Lesson[]>('/api/Lesson');
    return response.data;
  } catch (error) {
    console.error('Error fetching lessons:', error);
    throw error;
  }
};

/**
 * Get a specific lesson by ID
 */
export const fetchLessonById = async (lessonId: string): Promise<Lesson> => {
  try {
    const response = await api.get<Lesson>(`/api/Lesson/${lessonId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Get all lessons for a specific course
 */
export const fetchLessonsByCourseId = async (courseId: string): Promise<Lesson[]> => {
  try {
    // Get the course data which includes lessons with completion status
    const response = await api.get<any>(`/api/Course/${courseId}`);
    
    // Extract the lessons from the response, which should have the isCompleted property
    if (response.data && response.data.lessons) {
      return response.data.lessons;
    }
    
    // Fallback to the old method if the new format isn't available
    const lessons = await fetchLessons();
    return lessons.filter(lesson => lesson.courseId === courseId);
  } catch (error) {
    console.error(`Error fetching lessons for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Create a new lesson
 */
export const createLesson = async (lessonData: Partial<Lesson>): Promise<Lesson> => {
  try {
    // Generate a unique ID for the lesson if not provided
    const lesson = {
      ...lessonData,
      id: lessonData.id || crypto.randomUUID(),
      quizQuestions: lessonData.quizQuestions || []
    };
    
    const response = await api.post<Lesson>('/api/Lesson', lesson);
    return response.data;
  } catch (error) {
    console.error('Error creating lesson:', error);
    throw error;
  }
};

/**
 * Update an existing lesson
 */
export const updateLesson = async (lessonId: string, lessonData: Partial<Lesson>): Promise<Lesson> => {
  try {
    // Ensure orderIndex is a proper number, not a string or empty string
    const orderIndex = 
      typeof lessonData.orderIndex === 'string' 
        ? (lessonData.orderIndex === '' ? 0 : parseInt(lessonData.orderIndex, 10)) 
        : typeof lessonData.orderIndex === 'number' 
          ? lessonData.orderIndex 
          : 0;
    
    // Create the request data with ALL required fields from the C# record definition
    const requestData = {
      Title: lessonData.title || "",
      Content: lessonData.content || "",
      OrderIndex: orderIndex,
      // Always include QuizQuestions as an array, even if empty
      QuizQuestions: Array.isArray(lessonData.quizQuestions) && lessonData.quizQuestions.length > 0
        ? lessonData.quizQuestions.map(q => ({
            Id: q?.id || q?.Id || generateUuidV7(),
            QuestionText: q?.questionText || q?.QuestionText || q?.question || "",
            Answers: Array.isArray(q?.answers || q?.Answers || q?.options)
              ? (q?.answers || q?.Answers || q?.options || []).map(a => {
                  const answer = (a?.text || a?.answerText || a?.AnswerText || "").trim() || "Answer";
                  return {
                    Id: a?.id || a?.Id || generateUuidV7(),
                    text: answer,
                    AnswerText: answer,
                    IsCorrect: Boolean(a?.isCorrect || a?.IsCorrect)
                  };
                })
              : []
          }))
        : [] // Empty array, not omitted
    };
    
    console.log("Sending update data:", JSON.stringify(requestData));
    
    const response = await api.put<Lesson>(`/api/Lesson/${lessonId}`, requestData);
    return response.data;
  } catch (error) {
    console.error(`Error updating lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Delete a lesson
 */
export const deleteLesson = async (lessonId: string): Promise<void> => {
  try {
    await api.delete(`/api/Lesson/${lessonId}`);
  } catch (error) {
    console.error(`Error deleting lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Mark a lesson as complete
 */
export const completeLesson = async (lessonId: string): Promise<void> => {
  try {
    await api.post(`/api/Lesson/${lessonId}/complete`);
  } catch (error) {
    console.error(`Error completing lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Get lesson progress
 */
export const getLessonProgress = async (lessonId: string): Promise<any> => {
  try {
    const response = await api.get(`/api/Lesson/${lessonId}/progress`);
    return response.data;
  } catch (error) {
    console.error(`Error getting progress for lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Get quiz questions for a lesson
 */
export const fetchQuizQuestions = async (lessonId: string): Promise<QuizQuestion[]> => {
  try {
    const response = await api.get<QuizQuestion[]>(`/api/Quiz/lesson/${lessonId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching quiz for lesson ${lessonId}:`, error);
    return [];
  }
};

/**
 * Update lesson order indexes in batch
 */
export const updateLessonOrder = async (lessons: { id: string, orderIndex: number }[]): Promise<void> => {
  try {
    // Update each lesson with its new order index
    for (const lesson of lessons) {
      await api.put<Lesson>(`/api/Lesson/${lesson.id}/order`, {
        OrderIndex: lesson.orderIndex
      });
    }
  } catch (error) {
    console.error('Error updating lesson order:', error);
    throw error;
  }
}; 